/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from 'react';
import styled from 'styled-components';
import PostureModalLayout from 'components/Modal/PostureModal/PostureModalLayout';
import ModalPortal from 'components/Modal/ModalPortal';
import ChartLabel from 'components/Label/ChartLabel';
import useLineForDoughnut from 'hooks/useLineForDoughnut';
import { chartColor, color } from 'asserts/styles/colors';
import { commonTooltipOptions } from 'consts/chartOptions';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { CSSTransition } from 'react-transition-group';

import { ReactComponent as IconPostureKnee } from 'asserts/icons/Icon_Posture_Knee_2.svg';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

interface PostureStatisticsChartAProps {
  chartData: PostureData;
}

const PostureStatisticsCircleChart = ({
  chartData
}: PostureStatisticsChartAProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  const [isOPenModal, setIsOpenModal] = useState<boolean>(false);
  const [titleText, setTitleText] = useState<string>('');
  const [detailText, setDetailText] = useState<string>('');
  const [directions, setDirections] = useState<string[]>([]);

  const [selectedChartType, setSelectedChartType] = useState<string | null>(
    null
  );

  const legLabels = ['내반슬', '정상', '외반슬'];
  const chartType = ['oxleftleg', 'oxrightleg', 'leftfoot', 'rightfoot'];

  const { graphOrange, graphYellow, graphGreen } = chartColor;

  const LegColors = [graphYellow, graphGreen, graphOrange];

  const oxleftleg = chartData?.oxleftleg || [];
  const oxrightleg = chartData?.oxrightleg || [];

  const oxLeftLegData = [oxleftleg.varus, oxleftleg.normal, oxleftleg.valgus];
  const oxRightLegData = [
    oxrightleg.varus,
    oxrightleg.normal,
    oxrightleg.valgus
  ];

  const postureTypes = [
    {
      title: 'OX다리',
      detail: '좌',
      data: oxLeftLegData,
      icon: <IconPostureKnee />,
      labels: legLabels,
      colors: LegColors,
      gaugeLabels: legLabels
    },
    {
      title: 'OX다리',
      detail: '우',
      data: oxRightLegData,
      icon: <IconPostureKnee />,
      labels: legLabels,
      colors: LegColors,
      gaugeLabels: legLabels
    }
  ];

  const options = {
    layout: {
      padding: {
        top: 20,
        bottom: 20,
        right: 80,
        left: 80
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: commonTooltipOptions
    }
  };

  const handleClickOpenModal = (index: number) => {
    setIsOpenModal(true);
    const selectedChartType = chartType[index];

    setSelectedChartType(selectedChartType);
  };

  const handleClickCloseModal = () => {
    setIsOpenModal(false);
  };

  return (
    <>
      {postureTypes.map((posture, index) => (
        <PostureChart
          data={posture.data}
          key={index}
          icon={posture.icon}
          title={posture.title}
          detail={posture.detail}
          colors={posture.colors}
          labels={posture.labels}
          gaugeLabels={posture.gaugeLabels}
          options={options}
          setDirections={setDirections}
          setTitleText={setTitleText}
          setDetailText={setDetailText}
          onClickModal={() => handleClickOpenModal(index)}
        />
      ))}
      <CSSTransition
        in={isOPenModal}
        classNames="modal"
        timeout={300}
        nodeRef={nodeRef}
        unmountOnExit
      >
        <ModalPortal>
          <PostureModalLayout
            colors={LegColors}
            labels={legLabels}
            titleText={titleText}
            detailText={detailText}
            directions={directions}
            selectedChartType={selectedChartType}
            onClickModal={handleClickCloseModal}
          />
        </ModalPortal>
      </CSSTransition>
    </>
  );
};

const PostureChart = ({
  icon,
  title,
  detail,
  colors,
  options,
  labels,
  data: chartData,
  onClickModal,
  setTitleText,
  setDetailText,
  gaugeLabels,
  setDirections,
  directions
}: any) => {
  const data = {
    labels,
    datasets: [
      {
        label: `${title}Data`,
        data: chartData,
        backgroundColor: colors,
        borderColor: colors,
        circumference: 180,
        rotation: 270,
        cutout: '60%'
      }
    ]
  };

  const handleClickChart = (e: React.MouseEvent) => {
    setTitleText(title);
    setDetailText(detail);
    setDirections(directions);

    onClickModal();
    e.stopPropagation();
  };

  const labelsPlugin = {
    id: 'labelsPlugin',
    afterDatasetsDraw: useLineForDoughnut('half')
  };
  const uniqueLabels: string[] = Array.from(new Set(labels));
  const uniqueColos: string[] = Array.from(new Set(colors));

  return (
    <Wrapper onClick={handleClickChart}>
      <TitleWrapper>
        {icon}
        <TextBox>
          <PositionTitle>{title}</PositionTitle>
          <PositionDetail>{detail}</PositionDetail>
        </TextBox>
      </TitleWrapper>
      <ChartWrapper>
        <Doughnut data={data} options={options} plugins={[labelsPlugin]} />
        <GaugeLabels>
          {gaugeLabels.map((label: string) => (
            <GaugeLabel key={label}>{label}</GaugeLabel>
          ))}
        </GaugeLabels>
      </ChartWrapper>
      <ChartLabelWrapper>
        {uniqueLabels?.map((label: string, index: number) => (
          <ChartLabel
            key={`${title}${label}`}
            pointColor={uniqueColos[index]}
            text={label}
          />
        ))}
      </ChartLabelWrapper>
    </Wrapper>
  );
};

export default PostureStatisticsCircleChart;

const Wrapper = styled.div`
  position: relative;
  width: 600px;
  height: 272px;
  margin-bottom: 16px;
  background: ${color.white005};
  border-radius: 16px;
  cursor: pointer;
`;

const TitleWrapper = styled.div`
  display: flex;
  padding: 12px;
`;

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 12px;
  text-align: left;
`;

const PositionTitle = styled.p``;

const PositionDetail = styled.p`
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.3px;
  color: ${chartColor.graphMobile};
`;

const ChartWrapper = styled.div`
  position: absolute;
  top: -70px;
  left: 50%;
  transform: translateX(-50%);
  // height: 272px;
  width: 60%;
`;

const ChartLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 24px;
  width: 100%;
  justify-content: center;
`;

const GaugeLabels = styled.div`
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 54%;
`;

const GaugeLabel = styled.p`
  font-size: 13px;
  font-weight: 500;
  line-height: 16px; /* 123.077% */
  letter-spacing: -0.3px;
`;
